import React, { useState } from 'react';
import {
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Divider,
} from '@mui/material';
import { RealEstateSummary } from '../Components/Summary/RealEstateSummary';
import { IDocument } from '../../../../../../Interfaces/IDocument';
import { IMortgage } from '../../../../../../Interfaces/IMortgage';
import { CustomCircularProgress } from '../../../../../generic/CustomCircularProgress';
import { IPerson } from '../../../../../../Interfaces/IPerson';
import { MortgageDocumentSummary } from '../Components/Summary/MortgageDocumentSummary';
import { Search } from '@mui/icons-material';
import { MortgagePerson } from '../Components/Owner/MortgagePerson';
import { checkString } from '../../../../../core/standardFunctions';
import { MortgagePersonSummary } from '../Components/Summary/MortgagePersonSummary';

interface IProps {
  personArray: IPerson[];
  documentArray: IDocument[];
  mortgageObject: IMortgage;
  setMortgageObject: Function;
}

export const MortgageSummary: React.FC<IProps> = ({
  personArray,
  documentArray,
  mortgageObject,
  setMortgageObject,
}) => {
  const [isLoading] = useState(false);
  const [index, setIndex] = useState<number | null>(null);

  if (isLoading) {
    return <CustomCircularProgress />;
  } else {
    return (
      <>
        <Typography variant='h4' mb={2}>
          Abschluss
        </Typography>
        <Typography>
          Vielen Dank für die Eingabe Ihrer Daten. Bitte überprüfen Sie Ihre
          Daten nun noch einmal in der Übersicht. Wenn alles korrekt ist, senden
          Sie Ihre Daten bitte ab.
        </Typography>

        <Typography variant='h6' mt={6} mb={2}>
          Zusammenfassung
        </Typography>

        <Box ml={5} mr={5} mt={2}>
          <Typography variant='h6' sx={{ mt: 5 }}>
            Eigentümer
          </Typography>

          <Dialog
            open={index !== null}
            maxWidth='md'
            fullWidth
            onClose={() => setIndex(null)}
          >
            <DialogContent>
              <Box sx={{ mt: 2 }} />
              {index !== null && (
                <MortgagePerson
                  personArray={personArray}
                  setPersonArray={() => {}}
                  currentPerson={personArray[index]}
                  setCurrentPerson={() => {}}
                  mortgageObject={mortgageObject}
                  setMortgageObject={setMortgageObject}
                  setPersonHasDocument={() => {}}
                  setIsDisabled={() => {}}
                  isDisabled={false}
                  personHasDocument={[]}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant='outlined'
                onClick={() => {
                  setIndex(null);
                }}
              >
                Abbruch
              </Button>
            </DialogActions>
          </Dialog>

          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={'20%'}>Nr.:</TableCell>
                <TableCell width={'20%'}>Vorname</TableCell>
                <TableCell width={'20%'}>Nachname</TableCell>
                <TableCell width={'20%'}>Geburtstag</TableCell>
                <TableCell width={'20%'}>Aktionen</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {personArray.map((x, i) => (
                <TableRow>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{x.FirstName}</TableCell>
                  <TableCell>{x.LastName}</TableCell>
                  <TableCell>
                    {x.Birthday
                      ? x.Birthday.split('-').reverse().join('.')
                      : ''}
                  </TableCell>
                  <TableCell>
                    {
                      <IconButton
                        onClick={() => {
                          setIndex(i);
                        }}
                      >
                        <Search />
                      </IconButton>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Grid item xs={12}>
            <Divider sx={{ mt: 2, borderColor: 'text.primary' }} />
          </Grid>
        </Box>

        <MortgagePersonSummary
          beneficiary={mortgageObject.BeneficiaryDetails}
        />

        <RealEstateSummary mortgageObject={mortgageObject} />

        <Box ml={5} mt={2}>
          <Typography mb={2} variant='h6'>
            Details zur Grundschuld
          </Typography>

          <Grid container spacing={2} ml={8}>
            <>
              <Grid item sm={4}>
                <b>Neue bzw. zu löschende Grundschuld?</b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.ToBeDeleted ? 'Zu löschen' : 'Neu'}
              </Grid>

              <Grid item sm={4}>
                <b>
                  Wird die Grundschuld zur Finanzierung eines Immobilienkaufs
                  benötigt?
                </b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.PropertyForBuying ? 'Ja' : 'Nein'}
              </Grid>

              {mortgageObject.PropertyForBuying && (
                <>
                  <Grid item sm={4}>
                    <b>Ist der Immobilienkauf bereits erfolgt?</b>
                  </Grid>
                  <Grid item sm={8}>
                    {mortgageObject.PropertyOwned ? 'Ja' : 'Nein'}
                  </Grid>
                  {mortgageObject.PropertyOwned && (
                    <>
                      <Grid item sm={4}>
                        <b>Urkundenverzeichnisnummer:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(mortgageObject.DocumentIndexNumber)}
                      </Grid>

                      <Grid item sm={4}>
                        <b>Vorgangsnummer:</b>
                      </Grid>
                      <Grid item sm={8}>
                        {checkString(mortgageObject.OperationNumber)}
                      </Grid>
                    </>
                  )}
                </>
              )}

              <Grid item sm={4}>
                <b>Betrag der Grundschuld:</b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.AmountMortgage !== null
                  ? mortgageObject.AmountMortgage.toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                    })
                  : '0'}
              </Grid>

              <Grid item sm={4}>
                <b>Grundschuldzinssatz:</b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.MortgageInterest !== null
                  ? mortgageObject.MortgageInterest.toString()
                  : '0'}
                {' %'}
              </Grid>

              <Grid item sm={4}>
                <b>Nebenleistungen?</b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.AdditionalServices || 'Keine'}
              </Grid>

              <Grid item sm={4}>
                <b>Abschriften des Vertrages:</b>
              </Grid>
              <Grid item sm={8}>
                {mortgageObject.Transcripts?.map((transcript: string) => {
                  switch (transcript) {
                    case 'simple_transcription':
                      return 'Einfache Abschrift';

                    case 'certified_copy':
                      return 'Beglaubigte Abschrift';

                    case 'simple_execution':
                      return 'Einfache Ausfertigung';

                    case 'enforceable_copy':
                      return 'Vollstreckbare Ausfertigung';
                    default:
                      return 'Keine';
                  }
                }).join(', ') || 'Keine'}
              </Grid>
            </>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ mt: 2, mr: 5, borderColor: 'text.primary' }} />
          </Grid>

          <MortgageDocumentSummary documentArray={documentArray} />

          <Grid container spacing={2} mt={2}>
            <Grid item sm={4}>
              <b>Anmerkungen :</b>
            </Grid>
            <Grid item sm={8}>
              {mortgageObject.Remarks && mortgageObject.Remarks.length
                ? mortgageObject.Remarks
                : 'Keine'}
            </Grid>
          </Grid>
        </Box>
      </>
    );
  }
};
